export const FLOATING_PANEL_PADDING = 12;
export const FLOATING_PANEL_GAP = 8;

/**
 * Scrollport that should clip floating panels (editor body, report preview pane, etc.).
 *
 * @param {Element|null|undefined} anchorEl
 * @param {Element|null|undefined} [viewDom]
 * @returns {Element|null}
 */
export function findFloatingPanelClipRoot(anchorEl, viewDom) {
    const candidates = [
        anchorEl?.closest?.('.pm-editor-scroll-container'),
        viewDom?.closest?.('.pm-editor-scroll-container'),
        anchorEl?.closest?.('[data-report-scroll-root]'),
    ];
    for (const candidate of candidates) {
        if (candidate instanceof Element) return candidate;
    }
    return null;
}

/**
 * @param {Element|null} clipRoot
 * @returns {{left: number, top: number, right: number, bottom: number}}
 */
export function getFloatingPanelClipRect(clipRoot) {
    if (clipRoot instanceof Element) {
        const rect = clipRoot.getBoundingClientRect();
        return {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
        };
    }

    const pad = FLOATING_PANEL_PADDING;
    return {
        left: pad,
        top: pad,
        right: window.innerWidth - pad,
        bottom: window.innerHeight - pad,
    };
}

/**
 * @param {{left: number, top: number, right: number, bottom: number}} a
 * @param {{left: number, top: number, right: number, bottom: number}} b
 * @returns {boolean}
 */
export function rectsIntersect(a, b) {
    return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/**
 * @param {DOMRect|{left: number, top: number, right: number, bottom: number}} rect
 * @returns {{left: number, top: number, right: number, bottom: number}}
 */
export function normalizeRect(rect) {
    if (!rect) return null;
    if (typeof rect.right === 'number' && typeof rect.bottom === 'number') {
        return {
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
        };
    }
    const width = rect.width || 0;
    const height = rect.height || 0;
    return {
        left: rect.left,
        top: rect.top,
        right: rect.left + width,
        bottom: rect.top + height,
    };
}

/**
 * Resolve the on-screen anchor box for a floating panel.
 *
 * @param {{anchorEl?: Element|null, anchorPosition?: {left: number, top: number}|null, view?: object|null, position?: number|null}} args
 * @returns {{left: number, top: number, right: number, bottom: number}|null}
 */
export function resolvePopoverAnchorRect({anchorEl, anchorPosition, view, position}) {
    // Prefer the live DOM span — it tracks scroll correctly and matches what the user clicked.
    if (anchorEl && typeof anchorEl.getBoundingClientRect === 'function') {
        return normalizeRect(anchorEl.getBoundingClientRect());
    }

    if (view && typeof position === 'number') {
        try {
            const coords = view.coordsAtPos(position);
            return normalizeRect({
                left: coords.left,
                top: coords.top,
                right: coords.right,
                bottom: coords.bottom,
            });
        } catch (_error) {
            // fall through
        }
    }

    if (anchorPosition) {
        return normalizeRect({
            left: anchorPosition.left,
            top: anchorPosition.top,
            width: 0,
            height: 0,
        });
    }

    return null;
}

/**
 * Position a fixed panel above/below its anchor, clipped to a scroll container.
 * Returns visible:false when the anchor or panel would fall outside the clip rect.
 *
 * @param {object} args
 * @param {{left: number, top: number, right: number, bottom: number}|null} args.anchorRect
 * @param {number} args.panelWidth
 * @param {number} args.panelHeight
 * @param {{left: number, top: number, right: number, bottom: number}} args.clipRect
 * @param {number} [args.padding]
 * @param {number} [args.gap]
 * @returns {{visible: boolean, left: number, top: number}}
 */
export function computeFloatingPanelPosition({
    anchorRect,
    panelWidth,
    panelHeight,
    clipRect,
    padding = FLOATING_PANEL_PADDING,
    gap = FLOATING_PANEL_GAP,
}) {
    if (!anchorRect || !clipRect || panelWidth <= 0 || panelHeight <= 0) {
        return {visible: false, left: 0, top: 0};
    }

    if (!rectsIntersect(anchorRect, clipRect)) {
        return {visible: false, left: 0, top: 0};
    }

    const minLeft = clipRect.left + padding;
    const maxLeft = clipRect.right - panelWidth - padding;
    if (maxLeft < minLeft) {
        return {visible: false, left: 0, top: 0};
    }

    const centerX = (anchorRect.left + anchorRect.right) / 2;
    const left = Math.round(Math.min(maxLeft, Math.max(minLeft, centerX - panelWidth / 2)));

    const minTop = clipRect.top + padding;
    const maxTop = clipRect.bottom - panelHeight - padding;
    if (maxTop < minTop) {
        return {visible: false, left: 0, top: 0};
    }

    const topIfAbove = anchorRect.top - panelHeight - gap;
    const topIfBelow = anchorRect.bottom + gap;

    let top;
    if (maxTop >= minTop) {
        if (topIfAbove >= minTop && topIfAbove <= maxTop) {
            top = topIfAbove;
        } else if (topIfBelow >= minTop && topIfBelow <= maxTop) {
            top = topIfBelow;
        } else {
            top = Math.max(minTop, Math.min(maxTop, topIfAbove));
        }
    } else {
        top = Math.max(
            clipRect.top + padding,
            Math.min(topIfBelow, clipRect.bottom - panelHeight - padding),
        );
    }

    return {visible: true, left, top: Math.round(top)};
}

/**
 * @param {object} args
 * @param {Element|null|undefined} args.anchorEl
 * @param {{left: number, top: number}|null|undefined} args.anchorPosition
 * @param {object|null|undefined} args.view
 * @param {number|null|undefined} args.position
 * @param {number} args.panelWidth
 * @param {number} args.panelHeight
 * @param {Element|null|undefined} [args.viewDom]
 * @returns {{visible: boolean, left: number, top: number}}
 */
export function resolveFloatingPanelLayout({
    anchorEl,
    anchorPosition,
    view,
    position,
    panelWidth,
    panelHeight,
    viewDom,
}) {
    const clipRoot = findFloatingPanelClipRoot(anchorEl, viewDom || view?.dom);
    const clipRect = getFloatingPanelClipRect(clipRoot);
    const anchorRect = resolvePopoverAnchorRect({anchorEl, anchorPosition, view, position});
    const effectiveWidth = panelWidth > 0 ? panelWidth : 320;
    const effectiveHeight = panelHeight > 0 ? panelHeight : 120;

    return computeFloatingPanelPosition({
        anchorRect,
        panelWidth: effectiveWidth,
        panelHeight: effectiveHeight,
        clipRect,
    });
}
